import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, CheckCircle, Clock, Shield } from "lucide-react";

interface LiveStats {
  totalDeals: number;
  approvedToday: number;
  avgReviewTimeHours: number;
  complianceRate: number;
  activeDealValue: number;
}

export function LiveStatsWidget() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["demo-live-stats"],
    queryFn: async (): Promise<LiveStats> => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Get total deals
      const { count: totalDeals } = await supabase
        .from("deals")
        .select("*", { count: "exact", head: true });

      // Get deals approved today
      const { count: approvedToday } = await supabase
        .from("deals")
        .select("*", { count: "exact", head: true })
        .eq("status", "approved")
        .gte("updated_at", today.toISOString());

      // Get average review time (approved deals)
      const { data: approvedDeals } = await supabase
        .from("deals")
        .select("created_at, updated_at")
        .eq("status", "approved")
        .limit(100);

      let avgReviewTimeHours = 0;
      if (approvedDeals && approvedDeals.length > 0) {
        const reviewTimes = approvedDeals.map((deal) => {
          const created = new Date(deal.created_at);
          const updated = new Date(deal.updated_at);
          return (updated.getTime() - created.getTime()) / (1000 * 60 * 60); // hours
        });
        avgReviewTimeHours =
          reviewTimes.reduce((a, b) => a + b, 0) / reviewTimes.length;
      }

      // Get compliance rate (approved / total submitted)
      const { count: submittedCount } = await supabase
        .from("deals")
        .select("*", { count: "exact", head: true })
        .in("status", ["approved", "rejected"]);

      const { count: approvedCount } = await supabase
        .from("deals")
        .select("*", { count: "exact", head: true })
        .eq("status", "approved");

      const complianceRate =
        submittedCount && approvedCount
          ? (approvedCount / submittedCount) * 100
          : 0;

      // Get active deal value (approved deals total)
      const { data: activeDealData } = await supabase
        .from("deals")
        .select("amount")
        .eq("status", "approved");

      const activeDealValue =
        activeDealData?.reduce((sum, deal) => sum + (deal.amount || 0), 0) || 0;

      return {
        totalDeals: totalDeals || 0,
        approvedToday: approvedToday || 0,
        avgReviewTimeHours: Math.round(avgReviewTimeHours * 10) / 10,
        complianceRate: Math.round(complianceRate),
        activeDealValue,
      };
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <Card className="p-6 bg-gradient-to-br from-primary/5 via-background to-secondary/5 border-primary/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Live Demo Stats</h3>
          <Badge variant="outline" className="animate-pulse">
            Loading...
          </Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 bg-muted animate-pulse rounded" />
              <div className="h-8 bg-muted animate-pulse rounded" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-primary/5 via-background to-secondary/5 border-primary/20 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Live Demo Environment Stats
        </h3>
        <Badge variant="outline" className="border-green-500 text-green-600">
          <span className="relative flex h-2 w-2 mr-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          Live
        </Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total Deals */}
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Deals Processed
          </p>
          <p className="text-3xl font-bold text-foreground">
            {stats?.totalDeals || 0}
          </p>
          <p className="text-xs text-muted-foreground">All time</p>
        </div>

        {/* Approved Today */}
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Approved Today
          </p>
          <p className="text-3xl font-bold text-green-600">
            {stats?.approvedToday || 0}
          </p>
          <p className="text-xs text-muted-foreground">Last 24 hours</p>
        </div>

        {/* Avg Review Time */}
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Avg Review Time
          </p>
          <p className="text-3xl font-bold text-blue-600">
            {stats?.avgReviewTimeHours || 0}
            <span className="text-lg ml-1">hrs</span>
          </p>
          <p className="text-xs text-muted-foreground">
            {stats?.avgReviewTimeHours && stats.avgReviewTimeHours < 24
              ? "Within 24hr SLA"
              : "Processing"}
          </p>
        </div>

        {/* Compliance Rate */}
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            Compliance Rate
          </p>
          <p className="text-3xl font-bold text-primary">
            {stats?.complianceRate || 0}
            <span className="text-lg">%</span>
          </p>
          <p className="text-xs text-muted-foreground">
            {stats?.complianceRate && stats.complianceRate >= 95
              ? "Excellent"
              : "Good"}
          </p>
        </div>
      </div>

      {/* Active Deal Value (optional bottom row) */}
      {stats && stats.activeDealValue > 0 && (
        <div className="mt-4 pt-4 border-t border-border/50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Total Active Deal Value
            </p>
            <p className="text-xl font-bold text-foreground">
              ${stats.activeDealValue.toLocaleString()}
            </p>
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-muted-foreground text-center">
        Auto-refreshes every 30 seconds
      </div>
    </Card>
  );
}
